import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../utils/api';
import { setAuthData } from '../utils/auth';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Logo from '../components/Logo';

const RegisterPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    companyName: '',
    language: i18n.language || 'en',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/auth/register', formData);
      setAuthData(response.data.token, response.data.user);
      toast.success(t('qr_created'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_occurred'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-primary-50 to-white flex items-center justify-center px-6 py-12">
      <div className="absolute top-6 right-6">
        <LanguageSwitcher />
      </div>
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8" data-testid="logo-link">
          <Logo className="h-10 w-10" />
          <span className="text-3xl font-heading font-bold text-primary">cyQRus</span>
        </Link>
        <div className="card p-8 space-y-6" data-testid="register-card">
          <div className="space-y-2 text-center">
            <h1 className="text-2xl font-heading font-bold text-primary">{t('register')}</h1>
            <p className="text-sm text-muted-foreground">{t('get_started')}</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
            <div className="space-y-2">
              <Label htmlFor="companyName">{t('company_name')}</Label>
              <Input
                id="companyName"
                name="companyName"
                type="text"
                placeholder="Acme Inc."
                value={formData.companyName}
                onChange={handleChange}
                required
                data-testid="register-company-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t('email')}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="mail@example.com"
                value={formData.email}
                onChange={handleChange}
                required
                data-testid="register-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('password')}</Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={6}
                data-testid="register-password-input"
              />
            </div>
            <Button type="submit" className="w-full btn-primary" disabled={loading} data-testid="register-submit-btn">
              {loading ? t('loading') : t('register_button')}
            </Button>
          </form>
          <div className="text-center text-sm">
            <span className="text-muted-foreground">{t('already_have_account')} </span>
            <Link to="/login" className="text-accent hover:underline font-medium" data-testid="login-link">
              {t('login')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;